import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  WORK_SECTION_KEYS,
  fetchWorkPage,
  findSection,
} from "../services/contentApi";

const WorkPageContext = createContext(null);

export function WorkPageProvider({ children }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchWorkPage()
      .then((data) => {
        if (!cancelled) {
          setPage(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setPage(null);
        }
        console.error("Error fetching work page:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(() => {
    const result = {};
    WORK_SECTION_KEYS.forEach((key) => {
      result[key] = findSection(page, key);
    });
    return result;
  }, [page]);

  const value = useMemo(
    () => ({ page, sections, loading, error }),
    [page, sections, loading, error]
  );

  return (
    <WorkPageContext.Provider value={value}>
      {children}
    </WorkPageContext.Provider>
  );
}

export function useWorkPage() {
  const ctx = useContext(WorkPageContext);
  if (!ctx) {
    throw new Error("useWorkPage must be used within WorkPageProvider");
  }
  return ctx;
}

export function useWorkSection(key) {
  const ctx = useContext(WorkPageContext);
  if (!ctx) {
    return { section: null, loading: false, fromWorkPage: false };
  }
  return {
    section: ctx.sections[key] ?? null,
    loading: ctx.loading,
    fromWorkPage: true,
  };
}
